import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';

// A Puzzle Royale lobby a club admin created from ClubRoom. The lobby itself lives
// in cc-game-service; this row only carries the join code.
export interface ClubFunChallenge {
  room_id: string;
  share_code: string;
  battle_type: string;
  settings: Record<string, unknown>;
  expires_at: string;
  created_at: string;
  ended_at: string | null;
  created_by: number | null;
}

export interface RegisterFunChallengeBody {
  room_id: string;
  share_code: string;
  settings: Record<string, unknown>;
  expires_at: number;
}

export const ACTIVE_FUN_CHALLENGE_KEY = (clubId: number) =>
  ['clubs', 'funChallenge', clubId] as const;

export async function fetchActiveFunChallenge(
  clubId: number,
): Promise<{challenge: ClubFunChallenge | null}> {
  return (
    await ChessRestClient.get(`/api/clubs/${clubId}/fun-challenges/active/`)
  ).data;
}

export async function registerFunChallenge(
  clubId: number,
  body: RegisterFunChallengeBody,
): Promise<{challenge: ClubFunChallenge; duplicate: boolean}> {
  return (
    await ChessRestClient.post(`/api/clubs/${clubId}/fun-challenges/`, body)
  ).data;
}

/** The club's live fun challenge (or null). Drives both the admin's disabled
 *  Create button and every member's chat card. */
export const useActiveClubFunChallenge = (clubId: number) =>
  useQuery(ACTIVE_FUN_CHALLENGE_KEY(clubId), () =>
    fetchActiveFunChallenge(clubId),
  );

export const useRegisterClubFunChallenge = (clubId: number) => {
  const qc = useQueryClient();
  return useMutation(
    (body: RegisterFunChallengeBody) => registerFunChallenge(clubId, body),
    {
      onSuccess: data => {
        // Put the created card into Chat immediately, then reconcile in the
        // background. This avoids waiting for a refetch/socket round-trip.
        qc.setQueryData(ACTIVE_FUN_CHALLENGE_KEY(clubId), {
          challenge: data.challenge,
        });
        qc.invalidateQueries(ACTIVE_FUN_CHALLENGE_KEY(clubId));
      },
    },
  );
};
